import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { Plus, Map, Compass, User, Beer, MapPin, Clock, ChevronRight, Star, Trophy, Play, Calendar, Navigation, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API}/trips`);
      setTrips(response.data.trips || []);
    } catch (error) {
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'dive_bar': return <Beer className="w-3 h-3" />;
      case 'restaurant': return <span className="text-[10px]">🍔</span>;
      case 'landmark': return <span className="text-[10px]">🗿</span>;
      case 'hotel': return <span className="text-[10px]">🏨</span>;
      case 'gas_station': return <span className="text-[10px]">⛽</span>;
      default: return <MapPin className="w-3 h-3" />;
    }
  };

  // Calculate number of days based on "End of Day" stops
  const getTripDays = (stops) => {
    if (!stops || stops.length === 0) return 1;
    const endOfDayCount = stops.filter(s => s.estimated_time === -1).length;
    return endOfDayCount + 1;
  };

  // Estimate total miles for entire trip (all days combined)
  // Uses saved total_distance if available, otherwise estimates
  const getTripMiles = (trip) => {
    // Use saved calculation if available
    if (trip.total_distance && trip.total_distance > 0) {
      return trip.total_distance;
    }
    
    // Fallback to estimate
    if (!trip.stops || trip.stops.length === 0) return 0;
    
    const stopsWithLocation = trip.stops.filter(s => s.coordinates?.lat);
    if (stopsWithLocation.length === 0) return 0;
    
    // Calculate miles between all consecutive stops
    let totalMiles = 0;
    
    // Add miles from starting location to first stop if exists
    if (trip.starting_location?.lat) {
      totalMiles += 30; // Estimate 30 miles from start
    }
    
    // Add 30 miles between each consecutive stop (rough estimate)
    totalMiles += Math.max(0, (stopsWithLocation.length - 1) * 30);
    
    return totalMiles;
  };

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-24">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">Welcome back</p>
            <h1 className="font-oswald text-2xl font-bold uppercase">{user?.name || 'Rider'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {user?.is_premium && (
              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs font-bold rounded-full uppercase">
                Premium
              </span>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex justify-around">
            <div className="text-center">
              <div className="text-xl font-oswald font-bold text-bronze">{trips.length}</div>
              <div className="text-xs text-gray-500 uppercase">My Trips</div>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <div className="text-xl font-oswald font-bold text-cyan-400">
                {trips.reduce((acc, t) => acc + (t.stops?.length || 0), 0)}
              </div>
              <div className="text-xs text-gray-500 uppercase">Stops</div>
            </div>
            <div className="w-px bg-white/10" />
            <div className="text-center">
              <div className="text-xl font-oswald font-bold text-yellow-500">
                {trips.filter(t => t.is_public).length}
              </div>
              <div className="text-xs text-gray-500 uppercase">Shared</div>
            </div>
          </div>
        </div>

        {/* New Trip Button */}
        <Button
          data-testid="new-trip-btn"
          className="w-full h-14 bg-bronze hover:bg-bronze-600 font-oswald text-lg uppercase tracking-wider neon-bronze btn-primary mb-4"
          onClick={() => navigate('/trip/new')}
        >
          <Plus className="w-5 h-5 mr-2" />
          Plan New Ride
        </Button>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          <Button
            variant="outline"
            className="h-16 flex-col border-white/10 hover:bg-white/5 hover:border-bronze/50"
            onClick={() => navigate('/bars')}
          >
            <Beer className="w-5 h-5 mb-1 text-bronze" />
            <span className="text-[10px]">Dive Bars</span>
          </Button>
          <Button
            variant="outline"
            className="h-16 flex-col border-white/10 hover:bg-white/5 hover:border-cyan-500/50"
            onClick={() => navigate('/group-rides')}
          >
            <Users className="w-5 h-5 mb-1 text-cyan-400" />
            <span className="text-[10px]">Group Rides</span>
          </Button>
          <Button
            variant="outline"
            className="h-16 flex-col border-white/10 hover:bg-white/5 hover:border-purple-500/50"
            onClick={() => navigate('/leaderboard')}
          >
            <Trophy className="w-5 h-5 mb-1 text-purple-400" />
            <span className="text-[10px]">Leaderboard</span>
          </Button>
          <Button
            variant="outline"
            className="h-16 flex-col border-white/10 hover:bg-white/5 hover:border-yellow-500/50"
            onClick={() => navigate('/explore')}
          >
            <Compass className="w-5 h-5 mb-1 text-yellow-500" />
            <span className="text-[10px]">Explore</span>
          </Button>
        </div>

        {/* My Trips */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-oswald text-lg uppercase tracking-wide">My Trips</h2>
            {trips.length > 3 && (
              <span className="text-xs text-gray-500">{trips.length} trips</span>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass rounded-xl p-4 animate-pulse">
                  <div className="h-5 bg-white/10 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-white/10 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : trips.length === 0 ? (
            <div className="glass rounded-xl p-6 text-center">
              <Map className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No trips yet</p>
              <p className="text-gray-600 text-xs mt-1">Start planning your first adventure!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trips.map((trip) => {
                const days = getTripDays(trip.stops);
                const miles = getTripMiles(trip);
                const isEstimate = !(trip.total_distance && trip.total_distance > 0);
                
                return (
                  <div
                    key={trip.trip_id}
                    data-testid={`trip-card-${trip.trip_id}`}
                    className="glass rounded-xl p-4 trip-card cursor-pointer"
                    onClick={() => navigate(`/trip/${trip.trip_id}`)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-oswald font-semibold uppercase">{trip.title}</h3>
                          {!trip.is_public && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded uppercase">Private</span>
                          )}
                        </div>
                        {trip.description && (
                          <p className="text-gray-500 text-xs mt-1 line-clamp-1">{trip.description}</p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </div>
                    
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span>{trip.stops?.length || 0} stops</span>
                      </div>
                      <div className="flex items-center gap-1 text-cyan-400">
                        <Calendar className="w-3 h-3" />
                        <span>{days} {days === 1 ? 'day' : 'days'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-green-400">
                        <Navigation className="w-3 h-3" />
                        <span>{isEstimate ? '~' : ''}{miles} mi</span>
                      </div>
                      {trip.stops?.length > 0 && (
                        <div className="flex items-center gap-1 ml-auto">
                          {trip.stops.slice(0, 3).map((stop, idx) => (
                            <span key={idx} className={`category-${stop.category} px-1 py-0.5 rounded text-[10px]`}>
                              {getCategoryIcon(stop.category)}
                            </span>
                          ))}
                          {trip.stops.length > 3 && (
                            <span className="text-gray-600">+{trip.stops.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav glass safe-bottom">
        <div className="flex justify-around py-3">
          <button
            data-testid="nav-dashboard"
            className="flex flex-col items-center gap-1 text-bronze"
          >
            <Map className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">My Trips</span>
          </button>
          <button
            data-testid="nav-discover"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/discover')}
          >
            <Beer className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Discover</span>
          </button>
          <button
            data-testid="nav-leaderboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/leaderboard')}
          >
            <Trophy className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Ranks</span>
          </button>
          <button
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/profile')}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
